
interface VueInterface {
  activityDefinitions: ActivityDefinition[];
  dateRange: string[];
  dateRangeTextUtility(dateRange: string[]): string;
  getIconColor(job: Job, phaseTypeId: PipelinePhaseType | null): string;
  getPhaseDefinitions(phaseType: number, errorsOnlyFilter: boolean, job: Job): ActivityDefinition[];
  profile: IdTokenClaims;
  replayRequestService: ReplayRequestService;
  searchJobs(): void;
}

import ActivityDefinition from '@/models/activityDefinition';
import ActivityEvent from '@/models/activityEvent';
import { AxiosResponse, AxiosError } from 'axios';
import { bus } from '@/main';
import { DataTableHeader } from 'vuetify';
import { debounce } from '@/debounce';
import { HubConnectionState } from '@microsoft/signalr';
import { IdTokenClaims } from '@/auth/idTokenClaims';
import Job from '@/models/job';
import { JobClaim } from '@/models/jobClaim';
import { JobMetadata } from '@/models/jobMetadata';
import JobOperation from '@/models/jobOperation';
import {
  JobOperationType,
  PipelinePhaseType,
  PipelineStatus,
  StorageAccountType,
  TelemetryType,
} from '../models/enums';
import { mapActions, mapGetters, mapState } from 'vuex';
import ModelMetadataFile from '@/models/modelMetadataFile';
import ModelPublishEmail from './ModelPublishEmail.vue';
import ModelPublishFiles from './ModelPublishFiles.vue';
import ModelPublishSidebar from './ModelPublishSidebar.vue';
import ModelPublishTelemetry from './ModelPublishTelemetry.vue';
import PagedResult from '@/models/pagedResult';
import ReplayRequest from '@/models/replayRequest';
import ReplayRequestService from '@/services/replayRequestService';
import SelectObject from '@/models/selectObject';
import Telemetry from '@/models/telemetry';
import Vue from 'vue';
import { VueConstructor } from 'vue/types/vue';

const ModelPublish = (Vue as VueConstructor<Vue & VueInterface>).extend({
  props: {
    lightning: {
      type: Boolean,
      default: false,
    },
    lightningCatalogs: {
      type: Boolean,
      default: false,
    },
    packagePublishes: {
      type: Boolean,
      default: false,
    },
  },
  created() {
    this.replayRequestService = new ReplayRequestService();
    this.getFilters();
    this.getJobs();
    this.origin = window.location.origin;
    this.routeName = '/' + this.$route.name?.toLowerCase().replaceAll(' ', '');

    this.$store.subscribe((mutation) => {
      switch (mutation.type) {
        case 'activityDefinitionStore/updateActivityDefinitions':
          this.processActivityDefinitions();
          break;
      }
    });
    if (this.lightning) {
      bus.$emit('resetLightningCount');
    } else if (this.lightningCatalogs) {
      bus.$emit('newLightningCatalogCount');
    } else if (this.packagePublishes) {
      bus.$emit('resetFieldOrderzCount');
    } else {
      bus.$emit('resetMpCount');
    }
  },

  components: {
    'model-publish-sidebar': ModelPublishSidebar,
    'model-publish-telemetry': ModelPublishTelemetry,
    'model-publish-email': ModelPublishEmail,
    'model-publish-files': ModelPublishFiles,
  },

  mounted() {
    if (this.activityDefinitions && this.activityDefinitions.length > 0) {
      this.processActivityDefinitions();
    }

    // Set up job/phase duration update interval
    this.jobUpdateInterval = setInterval(() => {
      this.updateJobDurations();
    }, 1000);
    this.$once('hook:beforeDestroy', () => {
      clearInterval(this.jobUpdateInterval as number);
    });
  },

  beforeDestroy() {
  },

  data() {
    return {
      allEvents: [] as ActivityEvent[],
      dateRange: [] as string[],
      definitionsLoaded: false,
      filter: 'all',
      filterActiveOnlyJobs: false,
      filterCancelledJobs: false,
      filterCompanyNames: [] as string[],
      filterCompanyNameOptions: [] as SelectObject[],
      filterErrorJobs: false,
      filterProjectNames: [] as string[],
      filterProjectNameOptions: [] as SelectObject[],
      filterUserNames: [] as string[],
      filterUserNameOptions: [] as SelectObject[],
      jobs: [] as Job[],
      jobsAddedToSignalR: [] as number[],
      jobUpdateInterval: null as ReturnType<typeof setInterval> | number | null,
      jobsLoaded: false,
      origin: '',
      newJobCount: 0,
      newMetadata: [] as number[],
      PipelinePhaseType: PipelinePhaseType,
      receivedJobs: [] as number[],
      replayJobIds: [] as number[],
      routeName: '',
      search: '',
      selectedEvent: null as ActivityEvent | null,
      selectedJob: [] as Job[],
      showCancelJobWarning: false,
      showDateSearch: false,
      showEmailDialog: false,
      showFileDialog: false,
      showFilterCustomerMenu: false,
      showFilterPassMenu: false,
      showRunDbMaintenanceWarning: false,
      showSearch: false,
      sortBy: 'createDateTime',
      sortDesc: true,
      StorageAccountType: StorageAccountType,
      tableHeaders: [
        { text: 'Pass', value: 'pass', width: '86px', sortable: false },
        { text: 'Description', value: 'id', class: 'position-relative text-bolder' },
        { text: 'Customer', value: 'metadata', class: 'position-relative' },
        {
          text: 'Process',
          value: 'process',
          width: '50px',
          filterable: false,
          sortable: false,
          class: 'text-bolder',
        },
        {
          text: 'Duration',
          value: 'duration',
          width: '160px',
          align: 'end',
          filterable: false,
          sortable: false,
          class: 'text-bolder',
        },
        { text: 'Start', value: 'start', width: '200px', sortable: false, class: 'text-bolder' },
        { text: 'Platform', value: 'platform', sortable: false, class: 'text-bolder' },
      ] as DataTableHeader[],
      tableOptions: {
        page: 1,
        itemsPerPage: 30
      },
      telemetryLoaded: false,
      totalJobsCount: 0,
      showReplayModelBackupWarning: false,
      showMessageBoxDialog: false,
      messageBoxTitle: '',
      messageBoxText: '',
      messageBoxColor: 'success',
    };
  },

  computed: {
    ...mapState<IdTokenClaims>('userStore', {
      profile: 'profile',
    }),

    ...mapState<ActivityDefinition[]>('activityDefinitionStore', {
      activityDefinitions: 'jobActivityDefinitions',
    }),

    ...mapGetters<Record<string, string>>('activityDefinitionStore', {
      getPhaseDefinitions: 'getPhaseDefinitions',
    }),

    dateRangeText(): string {
      return this.dateRangeTextUtility(this.dateRange);
    },

    debouncedSearch() {
      return debounce(this.searchJobs, 1000);
    },

    filterCustomerActive(): boolean {
      return (
        this.filterCompanyNames.length > 0 || this.filterProjectNames.length > 0 || this.filterUserNames.length > 0
      );
    },

    isSingleMode(): boolean {
      return this.$route.params.jobId !== undefined;
    },
  },

  watch: {
    tableOptions: {
      handler() {
        if (!this.jobsLoaded) return;
        Vue.nextTick(() => {
          this.getJobs();
        });
      },
      deep: true,
    },
    search() {
      this.debouncedSearch();
    },
    dateRange() {
      if (this.dateRange.length === 0) {
        this.tableOptions.page === 1 ? this.getJobs() : (this.tableOptions.page = 1);
      }
    },
    activityDefinitions: 'processActivityDefinitions',
  },

  methods: {
    ...mapActions('activityDefinitionStore', [
      'clearJobActivityDefinitions',
      'commitJobActivityDefinitionEvents',
      'getActivityDefinitions',
    ]),

    assignJobProps(jobs: Job[]) {
      jobs.forEach((j) => {
        Vue.set(j, 'createDateTime', new Date(j.createDateTime));
        Vue.set(j, 'parsedMetadata', this.parseMetadata(j));
        Vue.set(j, 'newExtractEvent', false);
        Vue.set(j, 'newTransformEvent', false);
        Vue.set(j, 'newLoadEvent', false);
        Vue.set(j, 'activitiesLoaded', false);
        Vue.set(j, 'jobClaim', new JobClaim().fillFromJob(j));
      });
    },

    cancelPublishJob() {
      const operation = new JobOperation(
        false,
        this.selectedJob[0].id,
        JobOperationType.Stopped,
        new Date(),
        this.profile.name,
      );
      this.showCancelJobWarning = false;
      const job = this.jobs.find((x) => x.id === this.selectedJob[0].id);
      this.$http
        .post('joboperation?userAzureADId=' + this.profile.sub, operation)
        .then(() => {
          this.showCancelJobWarning = false;
          if (job) {
            Vue.set(job, 'cancelledDateTime', operation.operationDateTime);
            Vue.set(job, 'cancelledByUserId', this.profile.name);
            Vue.set(job, 'isCancelled', true);
            Vue.set(job, 'jobStatus', PipelineStatus.Cancelled);
          }
          bus.$emit('alert', 'Cancel Publish Sent', 'success', 6000);
        })
        .catch((error) => {
          bus.$emit('alert', 'Cancel Publish failed ', 'error', 6000, error);
        });
    },

    changedActiveOnlyJobs() {
      if (this.filterActiveOnlyJobs && this.filterCancelledJobs) {
        this.filterCancelledJobs = false;
      }
    },

    changedCancelledJobs() {
      if (this.filterActiveOnlyJobs && this.filterCancelledJobs) {
        this.filterActiveOnlyJobs = false;
      }
    },

    changeEventsLoaded(eventsLoaded: boolean) {
      this.selectedJob[0].eventsLoaded = eventsLoaded;
    },

    changedFilters() {
      this.showFilterCustomerMenu = this.showFilterPassMenu = false;
      let modelPipelineFilters = {
        filterActiveOnlyJobs: this.filterActiveOnlyJobs,
        filterCancelledJobs: this.filterCancelledJobs,
        filterErrorJobs: this.filterErrorJobs,
        filterCompanyNames: this.filterCompanyNames,
        filterProjectNames: this.filterProjectNames,
        filterUserNames: this.filterUserNames,
      };
      let page = 'modelpipeline';
      if (this.lightning) {
        page = 'lightning';
      } else if (this.lightningCatalogs) {
        page = 'lightningcatalogs';
      } else if (this.packagePublishes) {
        page = 'packagePublishes';
      }
      this.tableOptions.page = 1;
      localStorage.setItem(page + '-filters', JSON.stringify(modelPipelineFilters));
      this.getJobs();
    },

    checkJobPassesFilters(job: Job) {
      let metadata = this.parseMetadata(job);
      let jobPassesFilters =
        (this.filterActiveOnlyJobs && [1, 5].indexOf(job.jobStatus) >= 0) ||
        (!this.filterActiveOnlyJobs && [1, 5].indexOf(job.jobStatus) <= 0);
      jobPassesFilters =
        jobPassesFilters &&
        (this.filterCompanyNames.length == 0 || this.filterCompanyNames.indexOf(metadata.CompanyName) >= 0) &&
        (this.filterProjectNames.length == 0 || this.filterProjectNames.indexOf(metadata.ProjectName) >= 0) &&
        (this.filterUserNames.length == 0 || this.filterUserNames.indexOf(metadata.UserDisplayName) >= 0);
      return jobPassesFilters;
    },

    clearActiveOnlyJobFilter() {
      this.filterActiveOnlyJobs = false;
      this.changedFilters();
    },

    clearCancelledJobFilter() {
      this.filterCancelledJobs = false;
      this.changedFilters();
    },

    clearErrorJobFilter() {
      this.filterErrorJobs = false;
      this.changedFilters();
    },

    clearMetadataFlag(jobId: number) {
      this.newMetadata = this.newMetadata.filter((x) => x !== jobId);
    },

    clearSelectedEvent() {
      this.selectedEvent = null;
    },

    clearSelectedJob() {
      this.selectedJob = [];
      this.selectedEvent = null;
    },

    copyJobLink(id: number) {
      var jobLink = this.origin + this.routeName + '/' + id;
      navigator.clipboard.writeText(jobLink);
    },

    displayDate(date?: Date | string | null) {
      if (!date) return null;
      date = this.getDateAdjustedForUTC(date);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    },

    displayDuration(seconds: number): string {
      let hrs = Math.floor(seconds / 3600);
      let mins = Math.floor((seconds - (hrs * 3600)) / 60);
      seconds = Math.floor(seconds - (hrs * 3600) - (mins * 60));
      return (hrs > 0 ? hrs + ' hr ' : '') + mins + ' min ' + seconds + ' sec';
    },

    getAvatarInitials(jobClaim: JobClaim): string {
      let nameParts = jobClaim?.claimedByUserName?.split(' ');
      let initials = nameParts?.map((n) => {
        return n[0];
      });
      return initials?.join('').toUpperCase() ?? 'n/a';
    },

    getDateAdjustedForUTC(date?: Date | string | null) {
      const dateNoZ = date?.toString().indexOf('Z') === -1;
      return date ? new Date(date + (dateNoZ ? 'Z' : '')) : new Date();
    },

    getFilters() {
      let page = 'modelpipeline';
      if (this.lightning) {
        page = 'lightning';
      } else if (this.lightningCatalogs) {
        page = 'lightningcatalogs';
      } else if (this.packagePublishes) {
        page = 'packagePublishes';
      }
      let modelPipelineFilters = JSON.parse(localStorage.getItem(page + '-filters') || 'null');
      if (modelPipelineFilters) {
        this.filterActiveOnlyJobs = modelPipelineFilters.filterActiveOnlyJobs;
        this.filterCompanyNames = modelPipelineFilters.filterCompanyNames || [];
        this.filterProjectNames = modelPipelineFilters.filterProjectNames || [];
        this.filterUserNames = modelPipelineFilters.filterUserNames || [];
      }
    },

    getJobs() {
      this.newJobCount = 0;
      this.jobsAddedToSignalR = [] as number[];
      this.jobsLoaded = false;
      this.selectedJob = [];
      this.allEvents = [] as ActivityEvent[];
      this.selectedEvent = null;
      if (this.dateRange.length === 2) {
        if (new Date(this.dateRange[1]) < new Date(this.dateRange[0])) {
          this.dateRange = [this.dateRange[1], this.dateRange[0]];
        }
      }

      const query =
        'page=' + this.tableOptions.page +
        '&pageSize=' + this.tableOptions.itemsPerPage +
        '&searchTerm=' + encodeURIComponent(this.search) +
        '&sortBy=' + this.sortBy +
        '&sortDesc=' + !!this.sortDesc +
        '&startDate=' + (this.dateRange.length === 2 ? this.dateRange[0] : '') +
        '&endDate=' + (this.dateRange.length === 2 ? this.dateRange[1] : '') +
        (this.$route.params.jobId ? '&id=' + this.$route.params.jobId : '') +
        (this.filterCompanyNames.length > 0 ? '&filterCompanyNames=' + encodeURIComponent(this.filterCompanyNames.join('|')) : '') +
        (this.filterProjectNames.length > 0 ? '&filterProjectNames=' + encodeURIComponent(this.filterProjectNames.join('|')) : '') +
        (this.filterUserNames.length > 0 ? '&filterUserDisplayNames=' + encodeURIComponent(this.filterUserNames.join('|')) : '') +
        (this.filterActiveOnlyJobs ? '&filterActiveOnlyJobs=true' : '') +
        (this.filterCancelledJobs ? '&filterCancelledJobs=true' : '') +
        (this.filterErrorJobs ? '&filterErrorJobs=true' : '') +
        (this.lightning ? '&lightning=true' : '') +
        (this.lightningCatalogs ? '&lightningCatalogs=true' : '') +
        (this.packagePublishes ? '&packagePublishes=true' : '');
      if (!query) return;
      this.$http
        .get<never>('job?' + query)
        .then((result: AxiosResponse<PagedResult<Job>>) => {
          if (!this.isSingleMode) {
            this.populateFilters(result);
          }
            this.jobs = result.data.items;
          this.jobs.forEach((j) => (j.eventsLoaded = true));
          this.totalJobsCount = result.data.count;
          if (this.jobs.length > 0) {
            this.assignJobProps(this.jobs);
          }
          this.jobsLoaded = true;
          this.processActivityDefinitions();
          this.initializeJobDurations();
        })
        .catch((error: AxiosError) => {
          this.jobsLoaded = true;
          bus.$emit('alert', 'Get jobs failed ', 'error', 6000, error, false);
        });
    },

    getJobSize(job: Job) {
      let pm = job.parsedMetadata;
      let bytes = this.packagePublishes ? pm.AttachmentFileSizeBytes : pm.ModelFileSizeBytes;
      return (bytes / 1e6).toFixed(2);
    },

    getPassColor(job: Job) {
      switch (job.jobStatus) {
        case 1:
          return 'primary';
        case 2:
          return this.getIconColor(job, null);
        case 3:
          return 'error';
        case 4:
          return 'grey';
        case 5:
          return 'warning';
        case 6:
          return 'error';
      }
    },

    getPassIconName(job: Job) {
      switch (job.jobStatus) {
        case 1:
          return 'update';
        case 2:
          return 'done';
        case 3:
          return 'warning';
        case 4:
          return 'remove';
        case 5:
          return 'update';
        case 6:
          return 'update_disabled';
      }
    },

    getRef(id: number) {
      return this.origin + this.routeName + '/' + id;
    },

    getReplayRequests(jobs: Job[]) {
      jobs.forEach((j) => {
        this.replayRequestService.getReplayRequests(this.$http, j.id).then((result: ReplayRequest[]) => {
          this.updateReplayRequests(result, j);
        });
      });
    },

    goToBlobStorage(job: Job, storageAccountType: StorageAccountType) {
      this.$http
        .get<never>('modelmetadata?jobId=' + job.id)
        .then((result: AxiosResponse<ModelMetadataFile[]>) => {
          if (!result.data.length || !result.data[0].sasStoragePath) return;
          let regex = /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/;
          const companyId = result.data[0].sasStoragePath.match(regex);
          if (!companyId) return;
          let path: string | null = null;
          switch (storageAccountType) {
            case StorageAccountType.ModelData:
              path = process.env.VUE_APP_MODEL_DATA_BLOB_STORAGE_ACCOUNT || null;
              if (path !== null && companyId.length > 0) {
                path = path.replace('{CompanyId}', companyId[0]);
              }
              break;
            case StorageAccountType.ModelSync:
              path = process.env.VUE_APP_MODEL_SYNC_BLOB_STORAGE_ACCOUNT || null;
              break;
            case StorageAccountType.ImportBundle:
              path = process.env.VUE_APP_IMPORT_BUNDLE_BLOB_STORAGE_ACCOUNT || null;
              break;
          }
          if (path) window.open(path, '_blank');
        })
        .catch((error: AxiosError) => {
          bus.$emit('alert', 'Get files failed ', 'error', 6000, error, false);
        });
    },

    initializeJobDurations() {
      let inProgressStatuses = [PipelineStatus.Processing, PipelineStatus.Pending, PipelineStatus.FailedProcessing];
      for (let job of this.jobs) {
        if (job.isCancelled) continue;

        if (inProgressStatuses.includes(job.extractStatus)) {
          job.extractTime = (new Date().valueOf() - this.getDateAdjustedForUTC(job.extractStartDT).valueOf()) / 1e3;
        }
        if (inProgressStatuses.includes(job.loadStatus)) {
          job.loadTime = (new Date().valueOf() - this.getDateAdjustedForUTC(job.loadStartDT).valueOf()) / 1e3;
        }
        if (inProgressStatuses.includes(job.transformStatus)) {
          job.transformTime = (new Date().valueOf() - this.getDateAdjustedForUTC(job.transformStartDT).valueOf()) / 1e3;
        }
        if (inProgressStatuses.includes(job.jobStatus)) {
          job.jobTime = (new Date().valueOf() - this.getDateAdjustedForUTC(job.extractStartDT).valueOf()) / 1e3;
        }
      }
    },

    parseMetadata(job: Job): JobMetadata {
      const parsedData = job.metadata && this.isJSON(job.metadata) ? JSON.parse(job.metadata) : new JobMetadata();
      return parsedData;
    },

    populateFilters(result: AxiosResponse<PagedResult<Job>>) {
      let companyNameOptions = result.data.filterOptions.find((fo) => fo.filterType === 'CompanyName')?.options;
      this.filterCompanyNameOptions =
        companyNameOptions?.map((cn) => {
          return { text: cn, value: cn };
        }) || [];
      let projectNameOptions = result.data.filterOptions.find((fo) => fo.filterType === 'ProjectName')?.options;
      this.filterProjectNameOptions =
        projectNameOptions?.map((cn) => {
          return { text: cn, value: cn };
        }) || [];
      let userNamesOptions = result.data.filterOptions.find((fo) => fo.filterType === 'UserName')?.options;
      this.filterUserNameOptions =
        userNamesOptions?.map((cn) => {
          let userNameEmail: string[] = cn.split('|');
          return {
            text: userNameEmail[0] + ' (' + userNameEmail[1] + ')',
            value: userNameEmail[0],
          };
        }) || [];
    },

    processActivityDefinitions() {
      this.definitionsLoaded = false;
      if (!this.activityDefinitions) return;
      this.definitionsLoaded = true;
      this.jobs.forEach((job) => {
        if (this.$route.params.jobId) {
          if (this.selectedJob.length === 0) {
            this.selectedJob.push(this.jobs[0]);
          }
          if (this.$route.params.activityEventId) {
            let query = 'jobIds=' + this.selectedJob[0].id;
            query += '&activityEventId=' + this.$route.params.activityEventId.toString();
            this.$http.get<never>('activityEvent?' + query).then((result: AxiosResponse<ActivityEvent[]>) => {
              if (!result.data.length) return;
              this.selectEvent(result.data[0]);
            });
          }
        }

        job.eventsLoaded = true;
      });
    },

    replayModelBackup() {
      this.showReplayModelBackupWarning = true;
    },

    confirmReplayModelBackup() {
      this.showReplayModelBackupWarning = false;
      this.$http
        .post(`modelbackup?jobId=${this.selectedJob[0].id}&userGuid=${this.selectedJob[0].userGuid}`)
        .then(() => {
          this.showMessageBox('Success', 'Model Backup Replay Request Sent Successfully', 'success');
        })
        .catch((error) => {
          let errorMessage = 'Model Backup Replay Request Failed';
          if (error.response && error.response.data && error.response.data.message) {
            errorMessage = `Model Backup Replay Request Failed: ${error.response.data.message}`;
          }
          this.showMessageBox('Error', errorMessage, 'error');
        });
    },

    showMessageBox(title: string, message: string, color: string) {
      this.messageBoxTitle = title;
      this.messageBoxText = message;
      this.messageBoxColor = color;
      this.showMessageBoxDialog = true;
    },

    resumePublishJob() {
      const operation = new JobOperation(
        false,
        this.selectedJob[0].id,
        JobOperationType.Resumed,
        new Date(),
        this.profile.name,
      );
      this.$http
        .post('joboperation?userAzureADId=' + this.profile.sub, operation)
        .then(() => {
          bus.$emit('alert', 'Resume Publish Sent', 'success', 6000);
        })
        .catch((error) => {
          bus.$emit('alert', 'Resume Publish failed ', 'error', 6000, error);
        });
    },

    rowClick(item: Job, row: { select: (value: boolean) => void }) {
      row.select(true);
      item.newExtractEvent = false;
      item.newTransformEvent = false;
      item.newLoadEvent = false;
      this.clearJobActivityDefinitions();
      this.getActivityDefinitions({ jobId: item.id });
    },

    runDbMaintenance() {
      this.showRunDbMaintenanceWarning = false;
      if (!this.selectedJob.length) return;
      this.$http
        .post('runDbMaintenance'
          + '?jobId=' + this.selectedJob[0].id
          + '&userId=' + this.selectedJob[0].userGuid
        ).then(() => { }).catch((error) => {
          bus.$emit('alert', 'Run DB Maintenance failed ', 'error', 6000, error);
        });
    },

    searchJobs() {
      this.tableOptions.page === 1 ? this.getJobs() : (this.tableOptions.page = 1);
    },

    selectEvent(event: ActivityEvent) {
      this.selectedEvent = event;
      const def = this.activityDefinitions.find((ad) => ad.id === event.activityDefinitionId);
      this.selectedEvent.activityDefinitionName = def ? def.name : 'N/A';

      this.telemetryLoaded = false;

      this.$http
        .get<never>('telemetry?activityeventid=' + event.id)
        .then((result: AxiosResponse<Telemetry[]>) => {
          Vue.set(event, 'telemetryItems', result.data);
          let evtWarningCount = event.telemetryItems.some((x) => x.telemetryTypeId === TelemetryType.Warning);
          Vue.set(event, 'warningCount', evtWarningCount)
          let evtExceptionCount = event.telemetryItems.some((x) => x.telemetryTypeId === TelemetryType.Error);
          Vue.set(event, 'exceptionCount', evtExceptionCount);
          this.telemetryLoaded = true;
        })
        .catch((error: AxiosError) => {
          bus.$emit('alert', 'Get telemetry failed ', 'error', 6000, error);
        });
    },

    shareJob() {
      this.showEmailDialog = true;
    },

    toggleClaimedBy(job: Job) {
      let jobClaim = job.jobClaim.claimedByUserId
        ? new JobClaim()
        : new JobClaim(this.profile.sub, this.profile.name, this.profile.mail, new Date());
      Vue.set(job, 'jobClaim', jobClaim);
      let url = 'jobClaim?jobId=' + job.id;
      url += job.jobClaim.claimedByUserId ? '&userAzureADId=' + this.profile.sub : '';
      this.$http
        .put(url)
        .then((result: AxiosResponse<JobClaim>) => {
          Vue.set(job, 'jobClaim', new JobClaim().fillFromJobClaim(result.data));
        })
        .catch((error: AxiosError) => {
          bus.$emit('alert', 'Toggle job claimed by failed ', 'error', 6000, error);
        });
    },

    updateDateRange() {
      (this.$refs.showDateSearch as HTMLFormElement).save(this.dateRange);
      this.showDateSearch = false;
      this.getJobs();
    },

    updateJobDurations() {
      let inProgressStatuses = [PipelineStatus.Processing, PipelineStatus.Pending, PipelineStatus.FailedProcessing];
      let jobs = this.selectedEvent === null ? this.jobs : this.selectedJob;
      for (let job of jobs) {
        if (job.isCancelled) continue;
        if (inProgressStatuses.includes(job.extractStatus)) job.extractTime += 1;
        if (inProgressStatuses.includes(job.loadStatus)) job.loadTime += 1;
        if (inProgressStatuses.includes(job.transformStatus)) job.transformTime += 1;
        if (inProgressStatuses.includes(job.jobStatus)) job.jobTime += 1;
      }
    },

    updateReplayRequests(replayRequests: ReplayRequest[], job?: Job) {
      job = job ? job : this.selectedJob[0];
      replayRequests = this.sortArray(replayRequests, 'id');
      job.replayRequests = replayRequests;
      let definitionIds = replayRequests.map((x) => x.activityDefinitionId);
      Vue.set(job, 'replayedDefinitionIds', definitionIds);
    },

    viewAll() {
      this.$router.push(this.routeName);
      this.selectedEvent = null;
      this.selectedJob = [];
      this.getJobs();
    },
  },
});

export default ModelPublish;
